import { cn } from "../../lib/utils";
import { Link } from "react-router-dom";
import React, { createContext, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSidebarOpen } from "redux/slices/navigation";
import { motion } from "framer-motion";
// import { Menu, X } from "lucide-react";

import SidebarOrganizationDetails from "components/Sidebar/SidebarOrganizationDetails";
import SidebarProfileDetails from "components/Sidebar/SidebarProfileDetails";
import SidebarLogoutButton from "components/Sidebar/SidebarLogoutButton";

const SidebarContext = createContext(undefined);

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
};

export const SidebarProvider = ({
    children,
    open: openProp,
    setOpen: setOpenProp,
    animate = true,
}) => {
    const dispatch = useDispatch();
    const sidebarOpen = useSelector((state) => state.navigation.sidebarOpen);

    const open = openProp !== undefined ? openProp : sidebarOpen;
    
    const setOpen = (value) => {
        if (setOpenProp !== undefined) {
            setOpenProp(value);
        } else {
            dispatch(setSidebarOpen(value));
        }
    };

    return (
        <SidebarContext.Provider value={{ open, setOpen, animate }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const Sidebar = ({
    children,
    open,
    setOpen,
    animate,
}) => {
    return (
        <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
            {children}
        </SidebarProvider>
    );
};

export const SidebarBody = (props) => {
    return (
        <>
            <DesktopSidebar {...props} />
            {/* <MobileSidebar {...props} /> */}
        </>
    );
};

export const DesktopSidebar = ({
    className,
    children,
    isTouchDevice = false,
    ...props
}) => {
    const userOnboarded = useSelector((state) => state.user.onboarded);
    const dispatch = useDispatch();
    const { open, setOpen, animate } = useSidebar();

    useEffect(() => {
        if (isTouchDevice) {
            dispatch(setSidebarOpen(false));
        }
    }, [isTouchDevice, dispatch]);

    const hoverProps = isTouchDevice ? {} : { onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) };

    const collapsedWidth = isTouchDevice ? "80px" : "60px"; // Slightly wider for touch devices
    const expandedWidth = "300px";

    const sidebarWidth = isTouchDevice ? collapsedWidth : animate ? (open ? expandedWidth : collapsedWidth) : expandedWidth;

    return (
        <motion.div
            className={cn(
                "h-full py-2 hidden md:flex md:flex-col bg-zinc-800 w-[300px] flex-shrink-0",
                isTouchDevice ? "px-2" : "px-4",
                className
            )}
            initial={{
                width: sidebarWidth,
            }}
            animate={{
                width: sidebarWidth,
            }}
            transition={isTouchDevice ? { duration: 0 } : undefined}
            {...hoverProps}
            {...props}
        >
            {userOnboarded && <SidebarOrganizationDetails isTouchDevice={isTouchDevice} />}

            {children}

            <div className="mt-auto pt-3 space-y-1">
                {userOnboarded && <SidebarProfileDetails isTouchDevice={isTouchDevice} />}

                <SidebarLogoutButton isTouchDevice={isTouchDevice} />
            </div>
        </motion.div>
    );
};

/*
export const MobileSidebar = ({
    className,
    children,
    ...props
}) => {
    const { open, setOpen } = useSidebar();
    return (
        <>
            <div
                className={cn(
                    "h-10 px-4 py-4 flex flex-row md:hidden items-center justify-between bg-zinc-800 w-full"
                )}
                {...props}
            >
                <div className="flex justify-end z-20 w-full">
                    <Menu
                        className="text-neutral-800 dark:text-neutral-200 cursor-pointer"
                        onClick={() => setOpen(!open)}
                    />
                </div>
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ x: "-100%", opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "-100%", opacity: 0 }}
                            transition={{
                                duration: 0.3,
                                ease: "easeInOut",
                            }}
                            className={cn(
                                "fixed h-full w-full inset-0 bg-white dark:bg-neutral-900 p-10 z-[100] flex flex-col justify-between",
                                className
                            )}
                        >
                            <div
                                className="absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                onClick={() => setOpen(!open)}
                            >
                                <X />
                            </div>
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};
*/

export const SidebarLink = ({
    link,
    className,
    isTouchDevice = false,
    nested = false,
    ...props
}) => {
    const { open, animate, setOpen } = useSidebar();
    
    const HandleClick = () => {
        if (open) {
            setOpen(true);
        }
    };
    
    if (isTouchDevice === true) {
        return (
            <Link
                to={link.href}
                className={cn(
                    "flex flex-col items-center justify-center gap-0.5 py-2",
                    nested && "group/sidebar py-1.5",
                    className
                )}
                {...props}
            >
                {!nested && link.icon}

                <p className={cn(
                    "text-center text-xxs",
                    nested ? "text-neutral-400 transition-colors duration-150 group-hover/sidebar:text-white" : "text-white"
                )}>
                    {link.label}
                </p>
            </Link>
        );
    }

    if (isTouchDevice === false) {
        return (
            <Link
                to={link.href}
                className={cn(
                    "flex items-center justify-start gap-2 group/sidebar py-2",
                    nested && "gap-0 py-1.5 pl-8",
                    className
                )}
                onClick={HandleClick}
                {...props}
            >
                {!nested && (
                    <div className="ml-1">
                        {link.icon}
                    </div>
                )}
                
                <motion.span
                    initial={{
                        display: animate ? (open ? "inline-block" : "none") : "inline-block",
                        opacity: animate ? (open ? 1 : 0) : 1,
                    }}
                    animate={{
                        display: animate ? (open ? "inline-block" : "none") : "inline-block",
                        opacity: animate ? (open ? 1 : 0) : 1,
                    }}
                    className={cn(
                        "group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0",
                        nested ? "text-neutral-400 text-xs group-hover/sidebar:text-white" : "text-neutral-200 text-sm"
                    )}
                >
                    {link.label}
                </motion.span>
            </Link>
        );
    }
};